import React, { useContext, useMemo } from 'react';
import { Column } from 'react-table';
import { Portrait } from '@galaxy/lorule-react-commons';
import DataGrid from '../../common/datatables/DataGrid';
import { Event } from '../../../api/actionMemoApiTypes';
import LocalizationContext, { LocaleObject } from '../../../contexts/LocalizationContext';
import {
    formatLocalizedString,
    convertEmptyStringToDashes,
    formatDate,
} from '../../../utils/displayUtils';
import { SYSTEM_EMPLOYEE_ID, DATE_TIME_FORMAT } from '../../../utils/Constants';

const getLocaleKey = (localizedStrings: LocaleObject, eventType: string) => {
    const localeEntry = Object.entries(localizedStrings.actionMemo.event).find(
        ([key]) => key === eventType,
    );
    if (localeEntry) {
        return localeEntry[1];
    }
    return eventType;
};

const getAction = (localizedStrings: LocaleObject, event: Event) => {
    const eventProps = {
        amId: event.amId || '',
        eventId: event.eventId,
        eventOn: event.eventOn,

        eventBy: event.eventBy,
        eventByName: event.eventByName,
        eventByEmailAddress: event.eventByEmailAddress,
        eventByTitle: event.eventByTitle,

        proxyId: event.proxyId,
        proxyName: event.proxyName,
        proxyEmailAddress: event.proxyEmailAddress,
        proxyTitle: event.proxyTitle,

        eventType: event.eventType,

        segment1: event.segment1 || '',
        segment2: event.segment2 || '',
        segment3: event.segment3 || '',
        segment4: event.segment4 || '',
        segment5: event.segment5 || '',

        routedTo: event.routedTo || '',
        routedToName: event.routedToName || '',
        routedToEmailAddress: event.routedToEmailAddress || '',
        routedToTitle: event.routedToTitle || '',

        commentId: event.commentId || '',
        commentMessage: event.commentMessage || '',
    };

    let proxyAsName = 'System';
    if (event.eventBy !== event.proxyId) {
        proxyAsName = formatLocalizedString(
            localizedStrings,
            localizedStrings.actionMemo.event.PROXY_AS_USER,
            {
                ...eventProps,
            },
        );
    } else if (event.eventBy && event.eventBy !== SYSTEM_EMPLOYEE_ID) {
        proxyAsName = event.eventByName;
    }

    return formatLocalizedString(
        localizedStrings,
        getLocaleKey(localizedStrings, event.eventType),
        {
            ...eventProps,
            proxyAsName,
        },
    );
};

const HistoryTab = (props: { events: Event[] }) => {
    const localizedStrings = useContext(LocalizationContext);
    const { events } = props;

    const columns = React.useMemo(
        () => [
            {
                Header: '',
                accessor: 'eventByEmailAddress',
                Cell: (cellProps: { value: string }) =>
                    useMemo(
                        () => (
                            <div className="profile-photo">
                                {cellProps.value && (
                                    <Portrait username={cellProps.value} roundedCircle />
                                )}
                            </div>
                        ),
                        [],
                    ),
                disableSortBy: true,
                xs: 3,
                sm: 3,
                xl: 1,
            },
            {
                Header: 'Name/Title',
                accessor: 'eventByName',
                Cell: (cellProps: {
                    value: string;
                    row: {
                        original: {
                            eventByTitle: string;
                        };
                    };
                }) =>
                    useMemo(
                        () => (
                            <>
                                <div>{cellProps.value}</div>
                                <div className="font-italic font-weight-light">
                                    <small>{cellProps.row.original.eventByTitle}</small>
                                </div>
                            </>
                        ),
                        [],
                    ),
                xs: 9,
                sm: 9,
                xl: 2,
            },
            {
                Header: 'Action',
                accessor: 'action',
                xs: 6,
                sm: 6,
                xl: 3,
            },
            {
                Header: 'On',
                accessor: 'eventOn',
                Cell: (cellProps: { value: string }) =>
                    useMemo(() => <span>{formatDate(cellProps.value, DATE_TIME_FORMAT)}</span>, []),
                xs: 6,
                sm: 6,
                xl: 2,
            },
            {
                Header: 'Comment',
                accessor: 'commentMessage',
                Cell: (cellProps: { value: string }) => convertEmptyStringToDashes(cellProps.value),
                disableSortBy: true,
                xs: 12,
                sm: 12,
                xl: 4,
            },
        ],
        [],
    );

    const data = events.map((event) => ({
        ...event,
        eventByName:
            event.eventBy && event.eventBy !== SYSTEM_EMPLOYEE_ID ? event.eventByName : 'System',
        eventByTitle:
            event.eventBy && event.eventBy !== SYSTEM_EMPLOYEE_ID ? event.eventByTitle : '',
        action: getAction(localizedStrings, event),
    }));

    return (
        <div className="react-table px-2">
            <DataGrid
                columns={columns as Column<Object>[]}
                data={data}
                noGlobalFilter
                noPagination
                initialState={{ pageSize: 1000 }}
                mobileShowList
            />
        </div>
    );
};

export default HistoryTab;
